import React from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { sbg } from '../../theme/sbgTokens';

export interface SectionCardProps {
  /** `heading-4` title naming the one subject this card groups. */
  title?: React.ReactNode;
  /** Optional `body-xs` description under the title. */
  description?: React.ReactNode;
  /** A link, small button or ⋯ menu on the right of the header. */
  action?: React.ReactNode;
  /** Dense: 12px radius, 16px padding (tables, ledger panels). */
  dense?: boolean;
  /** Remove body padding — for full-bleed tables or lists inside the card. */
  flush?: boolean;
  /** Optional footer row (pagination, "View all"), separated by a hairline. */
  footer?: React.ReactNode;
  component?: React.ElementType;
  id?: string;
  'aria-label'?: string;
  sx?: SxProps<Theme>;
  bodySx?: SxProps<Theme>;
  children?: React.ReactNode;
}

/**
 * The standard card (book §11.8): white surface, 1px border, 16px radius,
 * no shadow. One subject per card; never nest more than one level.
 */
const SectionCard: React.FC<SectionCardProps> = ({
  title,
  description,
  action,
  dense = false,
  flush = false,
  footer,
  component = 'section',
  id,
  sx,
  bodySx,
  children,
  ...rest
}) => {
  const pad = dense ? 2 : 2.5;
  const hasHeader = Boolean(title || description || action);
  const headingId = id && title ? `${id}-title` : undefined;
  return (
    <Box
      component={component}
      id={id}
      aria-labelledby={headingId}
      aria-label={rest['aria-label']}
      sx={{
        bgcolor: sbg.bg.surface,
        border: `1px solid ${sbg.border.default}`,
        borderRadius: dense ? sbg.radius.lg : sbg.radius.card,
        minWidth: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      {hasHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
            px: pad,
            pt: pad,
            pb: flush ? pad : dense ? 1.5 : 2,
            ...(flush ? { borderBottom: `1px solid ${sbg.border.subtle}` } : null),
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title && (
              <Typography id={headingId} variant="h4" component="h2" sx={{ color: sbg.text.primary }}>
                {title}
              </Typography>
            )}
            {description && (
              <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: title ? 0.25 : 0 }}>
                {description}
              </Typography>
            )}
          </Box>
          {action && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, mt: -0.5 }}>{action}</Box>}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, ...(flush ? null : { px: pad, pb: pad, pt: hasHeader ? 0 : pad }), ...bodySx }}>
        {children}
      </Box>
      {footer && (
        <Box sx={{ borderTop: `1px solid ${sbg.border.subtle}`, px: pad, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          {footer}
        </Box>
      )}
    </Box>
  );
};

export default SectionCard;
